import React, { useState, useEffect, useRef } from "react";
import { useImpersonation } from "../../context/ImpersonationContext";
import apiClient from "../../apiClient";
import "./ViewAsDropdown.css";

const ALL_ROLES = [
  "admin", "vp", "director", "mum", "rm", "rma", "hr", "hr_director", "cb", "ta", "ld", "ld_director", "it",
];

function ViewAsDropdown() {
  const {
    isActive,
    mode,
    userRole,
    userName,
    impersonateRole,
    impersonateUser,
    exitImpersonation,
  } = useImpersonation();

  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch user list when dropdown opens (once)
  useEffect(() => {
    if (open && users.length === 0 && !loadingUsers) {
      setLoadingUsers(true);
      apiClient
        .get("/api/admin/users/list")
        .then((res) => setUsers(res.data.data || []))
        .catch(() => setUsers([]))
        .finally(() => setLoadingUsers(false));
    }
  }, [open, users.length, loadingUsers]);

  const filteredUsers = search.trim()
    ? users.filter((u) => {
        const q = search.toLowerCase();
        return (
          u.name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.role?.toLowerCase().includes(q)
        );
      })
    : users;

  const handleRoleSelect = (role) => {
    impersonateRole(role);
    setOpen(false);
  };

  const handleUserSelect = (user) => {
    impersonateUser(user);
    setOpen(false);
    setSearch("");
  };

  const handleExit = (e) => {
    e.stopPropagation();
    exitImpersonation();
    setOpen(false);
    setSearch("");
  };

  // Build the label shown on the active indicator
  const activeLabel = isActive
    ? mode === "user"
      ? `Viewing as: ${userName} (${userRole})`
      : `Viewing as role: ${userRole}`
    : null;

  return (
    <div className="view-as-container" ref={dropdownRef}>
      {/* Active indicator + exit */}
      {isActive && (
        <div className="view-as-indicator">
          <span className="view-as-indicator-text">{activeLabel}</span>
          <button
            className="view-as-exit-btn"
            onClick={handleExit}
            title="Exit impersonation"
          >
            ✕
          </button>
        </div>
      )}

      {/* Toggle button */}
      <button
        className={`view-as-toggle ${isActive ? "active" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        View As
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="view-as-dropdown">
          {/* Section: View as Role */}
          <div className="view-as-section-header">View as Role</div>
          <div className="view-as-role-list">
            {ALL_ROLES.map((role) => (
              <button
                key={role}
                className={`view-as-role-option ${
                  isActive && mode === "role" && userRole === role ? "selected" : ""
                }`}
                onClick={() => handleRoleSelect(role)}
              >
                {role}
              </button>
            ))}
          </div>

          <div className="view-as-divider" />

          {/* Section: View as User */}
          <div className="view-as-section-header">View as User</div>
          <input
            className="view-as-search"
            type="text"
            placeholder="Search by name, email, or role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="view-as-user-list">
            {loadingUsers && (
              <div className="view-as-loading">Loading users...</div>
            )}
            {!loadingUsers && filteredUsers.length === 0 && (
              <div className="view-as-empty">No users found</div>
            )}
            {filteredUsers.slice(0, 50).map((u) => (
              <button
                key={u.id}
                className={`view-as-user-option ${
                  isActive && mode === "user" && u.id === parseInt(String(u.id))
                    ? ""
                    : ""
                }`}
                onClick={() => handleUserSelect(u)}
              >
                <span className="view-as-user-name">{u.name}</span>
                <span className="view-as-user-email">{u.email}</span>
                <span className={`view-as-user-role-badge role-${u.role}`}>
                  {u.role}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewAsDropdown;
